import { useEffect } from 'react';
import useSettings from '../hooks/useSettings';
import getColorPresets from '../utils/getColorPresets';

/**
 * Escreve os atributos `data-*` e as variaveis de cor no <html> que os tokens de
 * design leem (theme/tokens.css e theme/global.css).
 *
 * POR QUE ISTO EXISTE, e nao o AppearanceProvider do handoff de design:
 * aquele componente guardava tema/destaque/densidade em localStorage proprio.
 * O sistema JA tem o SettingsContext, que persiste tema, preset de cor, direcao
 * e layout num cookie e alimenta o tema do MUI. Dois controles gravando no mesmo
 * <html> a partir de estados diferentes dao o pior tipo de defeito visual: o
 * tema "volta atras sozinho" quando um dos dois re-renderiza.
 *
 * Aqui nao existe estado: e projecao. A fonte da verdade continua sendo o
 * SettingsContext, e o painel de configuracao que ja existe (components/settings)
 * continua sendo o unico lugar que troca o tema.
 *
 * Efeito colateral pretendido: com `data-theme="dark"` no <html>, o modo escuro
 * do Tailwind tambem passa a valer (ver darkMode em tailwind.config.js), entao
 * MUI, tokens e shadcn escurecem juntos com um unico clique.
 */

/** Converte "#7635dc" nos canais que o shadcn espera: "H S% L%". */
function hexParaCanaisHsl(hex: string): string | null {
  const limpo = String(hex || '').trim().replace('#', '');
  if (limpo.length !== 6 && limpo.length !== 3) return null;

  const cheio =
    limpo.length === 3
      ? limpo
          .split('')
          .map((c) => c + c)
          .join('')
      : limpo;

  const r = parseInt(cheio.slice(0, 2), 16) / 255;
  const g = parseInt(cheio.slice(2, 4), 16) / 255;
  const b = parseInt(cheio.slice(4, 6), 16) / 255;
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  let h = 0;
  let s = 0;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Hex + alfa em 8 digitos. `0.09` -> "17", `0.26` -> "42". */
function comAlfa(hex: string, alfa: number): string {
  const byte = Math.round(Math.max(0, Math.min(1, alfa)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${byte}`;
}

/**
 * Propriedades que este componente escreve no <html>. Listadas para poder
 * REMOVER todas quando o preset volta para "default" - sem isso, trocar de roxo
 * para o padrao deixaria o roxo grudado, porque `style` inline vence o `:root`.
 */
const PROPRIEDADES = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--gps-accent',
  '--gps-accent-ink',
  '--gps-accent-soft',
  '--gps-accent-line',
];

export default function AparenciaTokens() {
  const { themeMode, themeColorPresets } = useSettings();

  useEffect(() => {
    const html = document.documentElement;

    // Só marca o escuro. No claro o atributo sai, para o :root valer sem
    // precisar de uma segunda declaracao - menos chance de os dois divergirem.
    if (themeMode === 'dark') html.setAttribute('data-theme', 'dark');
    else html.removeAttribute('data-theme');
  }, [themeMode]);

  /* --------------------------------------------------------------------------
     Preset de cor -> tokens do visual novo.

     ISTO E O QUE FALTAVA: a opcao "Cores" do painel de aparencia existia, salvava
     no cookie e alimentava o tema do MUI - mas as telas novas tiram o destaque de
     `--primary` (shadcn) e `--gps-accent`, que nao derivavam do preset. Trocar a
     cor mudava a aparencia de quase nada, e opcao que nao faz nada visivel e pior
     que opcao ausente.

     DUAS DECISOES:

     1. "default" NAO aplica `palette.light.primary`; ele LIMPA as propriedades e
        deixa o `:root` decidir. Os tokens do projeto (azul-marinho #1d3049 no
        claro, #8fb6de no escuro) foram escolhidos no redesign e nao sao iguais ao
        primary do template - forcar o preset default mudaria a cor de todo mundo
        que nunca tocou nesta opcao.

     2. No tema ESCURO usa-se `preset.light`, nao `preset.main`. Os tokens escuros
        do projeto seguem essa mesma regra por um motivo concreto: destaque escuro
        sobre fundo escuro nao se ve. O texto sobre o destaque acompanha - claro no
        tema claro, escuro no tema escuro - senao a cor de fundo muda e a do texto
        nao, que e como se produz botao ilegivel.
     -------------------------------------------------------------------------- */
  useEffect(() => {
    const html = document.documentElement;

    if (!themeColorPresets || themeColorPresets === 'default') {
      PROPRIEDADES.forEach((p) => html.style.removeProperty(p));
      return;
    }

    const preset = getColorPresets(themeColorPresets);
    const escuro = themeMode === 'dark';
    const cor = escuro ? preset?.light : preset?.main;
    if (!cor) {
      PROPRIEDADES.forEach((p) => html.style.removeProperty(p));
      return;
    }

    const canais = hexParaCanaisHsl(cor);
    if (canais) {
      html.style.setProperty('--primary', canais);
      // Texto sobre o destaque: branco no claro, quase preto no escuro (o mesmo
      // par que global.css usa nos dois temas).
      html.style.setProperty('--primary-foreground', escuro ? '214 33% 9%' : '0 0% 100%');
      // `--ring` e o anel de foco; no shadcn ele acompanha o destaque.
      html.style.setProperty('--ring', canais);
    }

    html.style.setProperty('--gps-accent', cor);
    html.style.setProperty('--gps-accent-ink', escuro ? '#10161f' : preset?.contrastText || '#ffffff');
    html.style.setProperty('--gps-accent-soft', comAlfa(cor, escuro ? 0.14 : 0.09));
    html.style.setProperty('--gps-accent-line', comAlfa(cor, escuro ? 0.34 : 0.26));
  }, [themeColorPresets, themeMode]);

  return null;
}
